import { useState } from 'react';
import Link from 'next/link';
import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';
import { Post } from 'lib/types';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { SimpleCard } from 'components/SimpleCard';
import { trackClick } from 'lib/gtag';
import { Carousel } from 'components/Carousel';
import { Help, Info } from '@mui/icons-material';
import { MobileTooltip } from 'components/MobileTooltip';
import { pluralize } from 'lib/helpers';

interface Props {
  post: Post;
  onChange: () => void;
}

export const PostMatches = ({
  post, onChange,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPostIds, setSelectedPostIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: matchPostsData } = useQuery<Post[]>({
    queryKey: ['getMatch', post.matchId],
    queryFn: () => API.getMatch(post.matchId!),
    enabled: !!post.matchId && !post.hasBoringThumbnail,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const matchPosts = matchPostsData?.filter(
    (s) => s.id !== post.id && !s.hasBoringThumbnail,
  ) || [];

  const { data: similarPostsData } = useQuery<Post[]>({
    queryKey: ['getSimilarPosts', post.id],
    queryFn: () => API.getSimilarPosts(post.id),
    enabled: !!post.id && !post.hasBoringThumbnail && !!matchPosts && !!currentUser,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const similarPosts = similarPostsData?.filter(
    (_p) => !matchPosts?.find((m) => m.id === _p.id) && !_p.hasBoringThumbnail,
  ) || [];

  const selectSimilarPost = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedPostIds((prev) => [
        ...prev,
        id,
      ]);
      return;
    }

    setSelectedPostIds((prev) => prev.filter((s) => s !== id));
  };

  const addPostsToMatch = async () => {
    trackClick('matchPost', { user: currentUser?.username });
    try {
      setSaving(true);
      const ids = [post.id].concat(selectedPostIds);
      await API.addPostsToMatch(ids, post?.matchId);
      setSaving(false);
      enqueueSnackbar('Post matched successfully.', { variant: 'success' });
      setSelectedPostIds([]);
      onChange();
    } catch (e) {
      setSaving(false);
      enqueueSnackbar('There was an error.  Please try again later.', { variant: 'error' });
    }
  };

  if (post.hasBoringThumbnail) return null;

  return (
    <>
      {post.matchId && (
        <div className="max-w-xs sm:max-w-full">
          <div className="border-t pt-4 flex flex-wrap justify-between items-center">
            <div className="flex items-center">
              <h5 className="font-semibold text-2xl mr-2">Matching Posts</h5>
              <MobileTooltip title="This post is part of a matching set.  See other posts in the set below.">
                <Help color="secondary" />
              </MobileTooltip>
            </div>
            <Link href={`/matches/${post.matchId}`} className="underline">
              View all matches
            </Link>
          </div>
          {matchPosts && matchPosts.length > 0 && <Carousel posts={matchPosts} />}
        </div>
      )}
      {similarPosts.length > 0 && (
        <>
          <div className="font-semibold border-t py-4 flex flex-wrap items-center justify-between">
            <div>
              <MobileTooltip
                placement="top"
                arrow
                className="mr-1"
                color="secondary"
                title="Did someone post your content? Upload an image to find matches across 20M+ social media posts on Gondola."
              >
                <Info />
              </MobileTooltip>
              <Link className="underline" href={{ pathname: '/visualmatch' }}>Visual Match AI</Link>

              {' '}
              found
              {' '}
              {similarPosts.length}
              {' '}
              similar
              {' '}
              {pluralize('post', similarPosts.length)}
              . Go ahead and match
              {' '}
              {pluralize('it', similarPosts.length, 'them')}
              !

            </div>
            <button
              type="button"
              disabled={selectedPostIds.length <= 0 || saving}
              className="btn-primary"
              onClick={addPostsToMatch}
            >
              Match posts
            </button>
          </div>
          <div className="overflow-x-scroll max-w-xs sm:max-w-none">
            <div className="flex gap-4 min-w-fit">
              {
                similarPosts.map((s) => (
                  <SimpleCard
                    key={s.id}
                    post={s}
                    onChange={(checked) => selectSimilarPost(s.id, checked)}
                    className="w-52"
                  />
                ))
              }
            </div>
          </div>
        </>
      )}
    </>
  );
};
